import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Wallet,
  Gift,
  Sparkles,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusMap = {
  pending: { label: "بانتظار الموافقة", color: "#F5A623", icon: Clock },
  approved: { label: "تمت الموافقة وتم الصرف", color: "#7ED321", icon: CheckCircle },
  rejected: { label: "تم الرفض", color: "#E85D45", icon: XCircle },
};

const Rewards = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/rewards/me`);
      setData(data);
    } catch {
      toast.error("فشل تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("الرجاء إدخال مبلغ صحيح");
      return;
    }
    if (amt > (data?.balance || 0)) {
      toast.error("المبلغ أكبر من رصيدك");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rewards/withdraw`, { amount: amt, note });
      toast.success("تم إرسال طلب السحب، سيتم مراجعته من قبل الإدارة");
      setShowForm(false);
      setAmount("");
      setNote("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل إرسال الطلب");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawAll = () => {
    setAmount(String(data?.balance || 0));
    setShowForm(true);
  };

  const hasPending = data?.withdrawals?.some((w) => w.status === "pending");

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <p className="text-[#5C6E64]">جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent pb-12">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#2D4A3E] to-[#1f3329] text-white">
        <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4 sm:px-6 py-5">
          <button
            onClick={() => navigate("/")}
            data-testid="back-home"
            className="flex items-center gap-2 mb-3 text-sm opacity-90 hover:opacity-100 transition-opacity"
          >
            <ArrowRight className="w-4 h-4" />
            رجوع
          </button>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <Gift className="w-7 h-7 text-[#F5A623]" />
            أرباحي
          </h1>
          <p className="text-xs opacity-80 mt-1">
            تكسب <span className="font-bold text-[#F5A623]">{fmt(data?.reward_per_item)} د.ع</span> عن كل قطعة تطلبها
          </p>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4 sm:px-6 py-6 space-y-5">
        {/* Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="rewards-balance"
          className="relative bg-gradient-to-br from-[#F5A623] to-[#E85D45] text-white rounded-3xl p-6 overflow-hidden shadow-lg"
        >
          <Sparkles className="absolute -top-4 -left-4 w-32 h-32 opacity-10" />
          <Wallet className="w-8 h-8 mb-3 opacity-90" />
          <p className="text-sm opacity-90 mb-1">رصيدك الحالي</p>
          <p className="font-bold text-4xl mb-1" data-testid="balance-amount">
            {fmt(data?.balance)}
          </p>
          <p className="text-xs opacity-90">دينار عراقي</p>

          <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/20">
            <div>
              <p className="text-[10px] opacity-80">إجمالي الطلبات</p>
              <p className="font-bold text-lg">{data?.total_orders}</p>
            </div>
            <div>
              <p className="text-[10px] opacity-80">إجمالي القطع</p>
              <p className="font-bold text-lg">{data?.total_items_ordered}</p>
            </div>
          </div>
        </motion.div>

        {/* Withdraw Buttons */}
        {hasPending ? (
          <div className="bg-[#FFF8E1] border border-[#F5A623]/40 rounded-2xl p-4 text-center">
            <Clock className="w-6 h-6 text-[#F5A623] mx-auto mb-2" />
            <p className="font-bold text-[#111814]">لديك طلب سحب قيد المعالجة</p>
            <p className="text-xs text-[#5C6E64] mt-1">سيتم مراجعته من قبل الإدارة قريباً</p>
          </div>
        ) : (data?.balance || 0) > 0 ? (
          showForm ? (
            <form onSubmit={handleWithdraw} className="bg-white border border-[#E4EBE6] rounded-2xl p-5 space-y-4">
              <h3 className="font-bold text-[#111814]">طلب سحب الأرباح</h3>
              <div>
                <label className="block text-sm font-medium text-[#111814] mb-2">المبلغ (د.ع)</label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  max={data?.balance}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="withdraw-amount-input"
                  placeholder={`الحد الأقصى: ${fmt(data?.balance)}`}
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111814] mb-2">ملاحظة (اختياري)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="withdraw-note-input"
                  placeholder="مثل: طريقة الاستلام المفضلة"
                  rows={2}
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setAmount("");
                  }}
                  className="flex-1 px-6 py-3 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#2D4A3E] font-medium rounded-full"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="withdraw-submit-btn"
                  className="flex-1 px-6 py-3 bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold rounded-full disabled:opacity-50"
                >
                  {submitting ? "جاري الإرسال..." : "إرسال الطلب"}
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleWithdrawAll}
                data-testid="withdraw-all-btn"
                className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2"
              >
                <Wallet className="w-4 h-4" />
                سحب الرصيد كامل
              </button>
              <button
                onClick={() => {
                  setAmount("");
                  setShowForm(true);
                }}
                data-testid="withdraw-partial-btn"
                className="bg-white border-2 border-[#2D4A3E] hover:bg-[#F3F6F4] text-[#2D4A3E] font-bold py-3.5 rounded-full transition-colors"
              >
                سحب مبلغ محدد
              </button>
            </div>
          )
        ) : (
          <div className="bg-[#F3F6F4] rounded-2xl p-5 text-center">
            <Package className="w-8 h-8 text-[#5C6E64] mx-auto mb-2" />
            <p className="font-bold text-[#111814]">لا يوجد رصيد للسحب</p>
            <p className="text-xs text-[#5C6E64] mt-1">
              اطلب أي منتج لتربح {fmt(data?.reward_per_item)} د.ع عن كل قطعة!
            </p>
            <Link
              to="/"
              data-testid="start-shopping-btn"
              className="inline-block mt-3 bg-[#2D4A3E] hover:bg-[#1f3329] text-white font-bold px-6 py-2.5 rounded-full text-sm"
            >
              ابدأ التسوق الآن
            </Link>
          </div>
        )}

        {/* Withdrawal History */}
        <section data-testid="withdrawals-history">
          <h2 className="font-heading font-bold text-lg text-[#111814] mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#2D4A3E]" />
            تاريخ السحوبات
          </h2>
          {!data?.withdrawals || data.withdrawals.length === 0 ? (
            <div className="bg-white border border-[#E4EBE6] rounded-2xl p-6 text-center">
              <p className="text-[#5C6E64] text-sm">لا توجد سحوبات سابقة</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.withdrawals.map((w) => {
                const StatusIcon = statusMap[w.status]?.icon || Clock;
                const color = statusMap[w.status]?.color || "#999";
                return (
                  <motion.div
                    key={w.id}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    data-testid={`history-${w.id}`}
                    className="bg-white border border-[#E4EBE6] rounded-2xl p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-bold text-[#111814]">{fmt(w.amount)} د.ع</p>
                      <p className="text-xs text-[#5C6E64]">{formatDate(w.created_at)}</p>
                    </div>
                    <span
                      className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5"
                      style={{ backgroundColor: `${color}20`, color }}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {statusMap[w.status]?.label}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Rewards;
